import { InvoiceCategoryFilter, InvoiceState } from '@treadinc/horizon-api-spec';

import { Invoice } from '~hooks/useInvoices/models';

import { TabQSParam } from '../DriverPay/TabQSParam';
import {
  DRIVER_PAY_QUERY_CONTEXT_PREFIX,
  INVOICES_QUERY_CONTEXT_PREFIX,
} from './constants';

export function makeInvoicesQueryContextKey(
  orderId: string,
  category: InvoiceCategoryFilter,
) {
  return `${INVOICES_QUERY_CONTEXT_PREFIX}_${orderId}_invoices_${category}`;
}

// Defaults to PAYABLES when no category has been chosen yet so the export button
// always has a concrete category to send to the API.
export function deriveExportCategory(
  invoiceCategory: InvoiceCategoryFilter | undefined,
): InvoiceCategoryFilter {
  return invoiceCategory ?? InvoiceCategoryFilter.PAYABLES;
}

// Picks the i18n key for the export-button label based on the active category
// so the user sees "Export Payables CSV" / "Export Receivables CSV" instead of
// a single generic label.
export function deriveExportButtonLabelKey(category: InvoiceCategoryFilter): string {
  return category === InvoiceCategoryFilter.RECEIVABLES
    ? 'approvals.export.export_receivables_csv'
    : 'approvals.export.export_payables_csv';
}

export function makeDriverPayQueryContextKey() {
  return `${DRIVER_PAY_QUERY_CONTEXT_PREFIX}_driver_pay`;
}

// Each driver-pay category remembers its own persisted date range.
// Mirrors DriverPayPageContext's category resolution — the data layer defaults
// to PAYABLES when the category param is absent.
export function driverPayDateRangeStorageKey(categoryParam: string | null) {
  const category =
    (categoryParam as InvoiceCategoryFilter) || InvoiceCategoryFilter.PAYABLES;

  return `driverPay_${category}_appliedDateFilters`;
}

export function hasActionsForInvoice(invoice: Invoice, selectedTab: TabQSParam | null) {
  const isInvoicesTab = selectedTab === TabQSParam.INVOICES || selectedTab === null;
  const isPendingTab = selectedTab === TabQSParam.PENDING;
  const isApprovedTab = selectedTab === TabQSParam.APPROVED;

  if (isPendingTab) {
    const isApprovable = Boolean(
      invoice.id &&
      [InvoiceState.PENDING, InvoiceState.CUSTOMER_PENDING].includes(invoice.state),
    );

    return isApprovable;
  }

  if (isApprovedTab) {
    const isSettled = Boolean(invoice.settlement);

    const isInSettableState = Boolean(
      invoice.id &&
      [InvoiceState.APPROVED, InvoiceState.CUSTOMER_APPROVED].includes(invoice.state),
    );

    return !isSettled && isInSettableState;
  }

  if (isInvoicesTab) {
    const isApprovable = Boolean(
      invoice.id &&
      [InvoiceState.PENDING, InvoiceState.CUSTOMER_PENDING].includes(invoice.state),
    );

    const isSettled = Boolean(invoice.settlement);
    const isInSettableState = Boolean(
      invoice.id &&
      [InvoiceState.APPROVED, InvoiceState.CUSTOMER_APPROVED].includes(invoice.state),
    );

    return isApprovable || (!isSettled && isInSettableState);
  }

  return false;
}
